import { BlockRenderer, type RenderProblem } from '../BlockRenderer';
import { useGlobalSection } from '../globalSections';
import { reportCms } from '../diagnostics';

/**
 * A live reference to a reusable section.
 *
 * ── IT RENDERS SOMEBODY ELSE'S DOCUMENT, DELIBERATELY ────────────────────────────────
 *
 * The block holds an id. The content comes from the referenced section's current
 * published version, bundled into the same CMS response the page already fetched. That
 * indirection is the entire feature: publishing a footer changes every page naming it,
 * because no page ever held a copy.
 *
 * ── IT FAILS ALONE ───────────────────────────────────────────────────────────────────
 *
 * §10/§92. A section that is missing, archived, unpublished or unrenderable produces
 * NOTHING and a diagnostic, and the page carries on. The ticket panel is not allowed to
 * disappear because a footer broke — and since the publish gate already refuses a page
 * whose references are dangling, reaching this path means something changed after
 * publication, which is exactly when the page must stay standing.
 *
 * Rendering is delegated to the same BlockRenderer the host uses, at the same context,
 * so a section's blocks are validated, version-checked and error-bounded identically to
 * every other block. There is no second renderer.
 */
export function GlobalSection({
  globalSectionId = null,
}: { globalSectionId?: string | null }) {
  const section = useGlobalSection(globalSectionId);

  if (!globalSectionId) return null;

  if (!section) {
    reportCms('cms_global_section_unresolved', {
      reason: 'not_in_payload', sectionId: globalSectionId,
    });
    return null;
  }

  const root = section.document?.root;
  if (!root) {
    reportCms('cms_global_section_unresolved', {
      reason: 'no_root', sectionId: globalSectionId,
    });
    return null;
  }

  return (
    <BlockRenderer
      node={root}
      context="global_section"
      onProblem={(problem: RenderProblem) => reportCms('cms_global_section_block_failed', {
        sectionId: globalSectionId, reason: problem.kind,
        nodeId: 'nodeId' in problem ? problem.nodeId : undefined,
      })}
    />
  );
}
